import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ArrowUp, ArrowDown, Save } from "lucide-react";
import { toast } from "sonner";

const empty = { key: "", label_en: "", label_ar: "", type: "phone", parent: "", active: true };

export default function CategoriesManager() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(empty);

  const load = async () => setCats((await api.get("/admin/categories")).data);
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.key || !form.label_en) return toast.error("Key & English label required");
    try {
      await api.post("/admin/categories", { ...form, order: cats.length });
      setForm(empty);
      await load();
      toast.success("Category added");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const save = async (c) => {
    try {
      await api.put(`/admin/categories/${c.id}`, {
        key: c.key, label_en: c.label_en, label_ar: c.label_ar || "",
        type: c.type || "phone", parent: c.parent || "",
        order: c.order ?? 999, active: c.active !== false,
      });
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete category "${c.label_en}"?`)) return;
    try { await api.delete(`/admin/categories/${c.id}`); await load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const move = async (idx, dir) => {
    const t = idx + dir;
    if (t < 0 || t >= cats.length) return;
    const ids = cats.map(c => c.id);
    [ids[idx], ids[t]] = [ids[t], ids[idx]];
    try { await api.put("/admin/categories/reorder", { ordered_ids: ids }); await load(); }
    catch { toast.error("Reorder failed"); }
  };

  const patchLocal = (id, patch) => setCats(cs => cs.map(c => c.id === id ? { ...c, ...patch } : c));

  return (
    <div className="space-y-6" data-testid="categories-manager">
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h3 className="font-display text-base font-semibold text-[#0B132B] mb-3">Add Category</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
          <Input placeholder="Key (e.g. tablets)" value={form.key} onChange={e => setForm({ ...form, key: e.target.value.toLowerCase().replace(/\s+/g, "_") })} data-testid="cat-key-input" />
          <Input placeholder="Label EN" value={form.label_en} onChange={e => setForm({ ...form, label_en: e.target.value })} data-testid="cat-labelen-input" />
          <Input placeholder="Label AR" value={form.label_ar} onChange={e => setForm({ ...form, label_ar: e.target.value })} data-testid="cat-labelar-input" />
          <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
            <SelectTrigger data-testid="cat-type-input"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="phone">Phone</SelectItem>
              <SelectItem value="accessory">Accessory</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={add} className="rounded-full bg-[#1E5FD9] hover:bg-[#174AB0]" data-testid="cat-add-btn"><Plus className="w-4 h-4 me-1" /> Add</Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl divide-y">
        {cats.length === 0 && <p className="p-8 text-center text-slate-500">No categories yet.</p>}
        {cats.map((c, idx) => (
          <div key={c.id} className="p-4 flex flex-wrap items-center gap-2" data-testid={`cat-row-${c.id}`}>
            <div className="flex flex-col gap-1">
              <Button size="icon" variant="outline" className="rounded-full w-8 h-8" disabled={idx === 0} onClick={() => move(idx, -1)} aria-label="Up"><ArrowUp className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="outline" className="rounded-full w-8 h-8" disabled={idx === cats.length - 1} onClick={() => move(idx, 1)} aria-label="Down"><ArrowDown className="w-3.5 h-3.5" /></Button>
            </div>
            <Input value={c.key} onChange={e => patchLocal(c.id, { key: e.target.value })} className="w-32" />
            <Input value={c.label_en} onChange={e => patchLocal(c.id, { label_en: e.target.value })} className="flex-1 min-w-[140px]" data-testid={`cat-labelen-${c.id}`} />
            <Input dir="rtl" value={c.label_ar || ""} onChange={e => patchLocal(c.id, { label_ar: e.target.value })} className="flex-1 min-w-[140px]" />
            <Select value={c.type || "phone"} onValueChange={v => patchLocal(c.id, { type: v })}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="phone">Phone</SelectItem>
                <SelectItem value="accessory">Accessory</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500">Active</Label>
              <Switch checked={c.active !== false} onCheckedChange={v => patchLocal(c.id, { active: v })} />
            </div>
            <Button size="sm" onClick={() => save(c)} className="rounded-full bg-[#1E5FD9] hover:bg-[#174AB0]" data-testid={`cat-save-${c.id}`}><Save className="w-3.5 h-3.5 me-1" /> Save</Button>
            <Button size="sm" variant="outline" onClick={() => del(c)} className="rounded-full text-rose-600 border-rose-200" data-testid={`cat-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
